import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  FaTimes,
  FaFileExcel,
  FaFileAlt,
  FaExternalLinkAlt,
  FaDownload,
  FaDatabase,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import axiosInstance from "api/axiosInstance";
import config from "../../../config.js";

export default function AdminCountModal({
  isOpen,
  closeModal,
  modalData,
}) {
  const accessToken = useSelector(
    (state) => state.auth.accessToken
  );

  const BASE_URL = config.cdnUrl;

  const [structuredFiles, setStructuredFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const type = modalData?.type;
  const title = modalData?.title;
  const count = modalData?.count;
  const data = modalData?.data;
  const files = modalData?.files || [];

  /*
   * دریافت فایل‌های ساختاریافته
   */
  useEffect(() => {
    if (!isOpen || type !== "structured" || !data?.id) {
      return;
    }

    const getStructuredFiles = async () => {
      setLoading(true);
      setError("");
      setStructuredFiles([]);

      try {
        const res = await axiosInstance.get(
          `admin/businesses/${data.id}/structured-files`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const result = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        setStructuredFiles(result);
      } catch (err) {
        console.error(
          "خطا در دریافت فایل‌های ساختاریافته:",
          err
        );

        setError(
          "دریافت فایل‌های ساختاریافته با خطا مواجه شد."
        );
      } finally {
        setLoading(false);
      }
    };

    getStructuredFiles();
  }, [isOpen, type, data?.id, accessToken]);

  /*
   * پاک کردن اطلاعات هنگام بسته شدن Modal
   */
  useEffect(() => {
    if (!isOpen) {
      setStructuredFiles([]);
      setError("");
      setLoading(false);
      setDownloadLoading(false);
    }
  }, [isOpen]);

  /*
   * ساخت URL فایل
   */
  const getFileUrl = (file) => {
    if (!file?.url) return "#";

    const baseUrl = BASE_URL?.endsWith("/")
      ? BASE_URL.slice(0, -1)
      : BASE_URL;

    const fileUrl = file.url.startsWith("/")
      ? file.url
      : `/${file.url}`;

    return `${baseUrl}${fileUrl}`;
  };

  /*
   * آیکون فایل
   */
  const getFileIcon = (extension) => {
    const ext = extension?.toLowerCase();

    if (ext === "xlsx" || ext === "xls") {
      return (
        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
          <FaFileExcel className="text-green-600 text-xl" />
        </div>
      );
    }

    return (
      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
        <FaFileAlt className="text-gray-500 text-xl" />
      </div>
    );
  };

  /*
   * دانلود فایل چانک‌ها
   *
   * GET:
   * /{businessId}/chunks/export
   */
  const handleDownloadChunks = async () => {
    if (!data?.id) {
      return;
    }

    try {
      setDownloadLoading(true);

      const response = await axiosInstance.get(
        `admin/businesses/${data.id}/chunks/export`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: "blob",
        }
      );

      /*
       * ساخت فایل قابل دانلود
       */
      const blob = new Blob(
        [response.data],
        {
          type:
            response.headers?.["content-type"] ||
            "application/octet-stream",
        }
      );

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      /*
       * اگر بک‌اند Content-Disposition داشته باشد
       * اسم فایل را از آن می‌گیریم.
       */
      const contentDisposition =
        response.headers?.["content-disposition"];

      let fileName = `chunks-${data.username || data.id}.xlsx`;

      if (contentDisposition) {
        const match =
          contentDisposition.match(
            /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i
          );

        if (match?.[1]) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(
        "خطا در دانلود فایل چانک‌ها:",
        err
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  /*
   * محتوای Modal
   */
  const renderContent = () => {
    /*
     * ==================================
     * KNOWLEDGE FILES
     * ==================================
     */
    if (type === "knowledge") {
      return (
        <div>
          <div className="bg-gray-50 border rounded-xl p-4 mb-5">
            <p className="text-sm text-gray-500">
              تعداد فایل‌های پایگاه دانش
            </p>

            <p className="text-2xl font-bold text-gray-800 mt-1">
              {count || 0}
            </p>
          </div>

          {files.length === 0 ? (
            <div className="text-center text-gray-500 py-10">
              فایل پایگاه دانشی وجود ندارد.
            </div>
          ) : (
            <div className="space-y-3">
              {files.map((file, index) => {
                const fileUrl = getFileUrl(file);

                return (
                  <div
                    key={file.id || index}
                    className="border rounded-xl p-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        {getFileIcon(file.extension)}

                        <div className="min-w-0">
                          <p
                            className="font-medium text-gray-800 truncate"
                            title={file.name}
                          >
                            {file.name}
                          </p>

                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span>
                              .{file.extension}
                            </span>

                            <span>
                              {file.usedTokens || 0} توکن
                            </span>
                          </div>
                        </div>
                      </div>

                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition"
                      >
                        <FaExternalLinkAlt className="text-xs" />

                        <span>
                          باز کردن
                        </span>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    /*
     * ==================================
     * STRUCTURED FILES
     * ==================================
     */
    if (type === "structured") {
      return (
        <div>
          <div className="bg-gray-50 border rounded-xl p-4 mb-5">
            <p className="text-sm text-gray-500">
              تعداد فایل‌های ساختاریافته
            </p>

            <p className="text-2xl font-bold text-gray-800 mt-1">
              {count || 0}
            </p>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-9 h-9 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />

              <span className="mt-4 text-sm text-gray-500">
                در حال دریافت فایل‌ها...
              </span>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-center">
              {error}
            </div>
          )}

          {!loading &&
            !error &&
            structuredFiles.length === 0 && (
              <div className="border rounded-xl py-12 text-center">
                <FaFileAlt className="mx-auto text-gray-300 text-4xl mb-3" />

                <p className="text-gray-500">
                  فایل ساختاریافته‌ای وجود ندارد.
                </p>
              </div>
            )}

          {!loading &&
            !error &&
            structuredFiles.length > 0 && (
              <div className="space-y-3">
                {structuredFiles.map((file, index) => {
                  const fileUrl = getFileUrl(file);

                  return (
                    <div
                      key={file.id || index}
                      className="border rounded-xl p-4 hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          {getFileIcon(file.extension)}

                          <div className="min-w-0">
                            <p
                              className="font-medium text-gray-800 truncate"
                              title={file.name}
                            >
                              {file.name}
                            </p>

                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span>
                                .{file.extension}
                              </span>

                              <span>
                                {file.usedTokens || 0} توکن
                              </span>
                            </div>
                          </div>
                        </div>

                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition"
                        >
                          <FaExternalLinkAlt className="text-xs" />

                          <span>
                            باز کردن
                          </span>
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      );
    }

    /*
     * ==================================
     * CHUNKS
     * ==================================
     */
    if (type === "chunk") {
      return (
        <div>
          <div className="bg-gray-50 border rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                <FaDatabase className="text-purple-600 text-2xl" />
              </div>

              <div>
                <p className="text-sm text-gray-500">
                  تعداد چانک‌های تولید شده
                </p>

                <p className="text-3xl font-bold text-gray-800 mt-1">
                  {count || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 border rounded-xl p-5 text-center">
            <p className="text-sm text-gray-500 mb-4">
              برای دریافت تمام چانک‌های این کسب‌وکار،
              فایل خروجی را دانلود کنید.
            </p>

            <button
              type="button"
              onClick={handleDownloadChunks}
              disabled={
                downloadLoading || !count
              }
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition"
            >
              {downloadLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />

                  <span>
                    در حال آماده‌سازی فایل...
                  </span>
                </>
              ) : (
                <>
                  <FaDownload />

                  <span>
                    دانلود کل چانک‌ها
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center text-gray-500 py-10">
        اطلاعاتی برای نمایش وجود ندارد.
      </div>
    );
  };

  if (!modalData) {
    return null;
  }

  return (
    <Transition show={isOpen}>
      <Dialog
        as="div"
        className="relative z-[9999]"
        onClose={closeModal}
      >
        <TransitionChild
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px]" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden rtl">

              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div>
                  <DialogTitle className="text-lg font-bold text-gray-800">
                    {title}
                  </DialogTitle>

                  {data?.username && (
                    <p className="text-xs text-gray-400 mt-1">
                      کسب‌وکار: {data.username}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="p-5 max-h-[70vh] overflow-y-auto">
                {renderContent()}
              </div>

              <div className="px-5 py-4 border-t bg-gray-50 flex justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
                >
                  بستن
                </button>
              </div>

            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}